import React, { useEffect } from "react";
import useScreenRotation from "./useScreenRotation";
import MapSlide from "./MapSlide";
import {
  ScreenLogoBar, ScreenSkyline, FallbackBackground, LoadingSlide, EmptySlide,
  ProgressDot, formatPubDate, CalendarDays,
} from "./ScreenParts";

function useScreenChrome() {
  useEffect(() => {
    const tag = document.createElement("meta");
    tag.name = "robots";
    tag.content = "noindex, nofollow";
    document.head.appendChild(tag);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.head.removeChild(tag);
      document.body.style.overflow = prev;
    };
  }, []);
}

export default function ScreenNewsVertical() {
  useScreenChrome();
  return <VerticalShell />;
}

function VerticalShell() {
  const { current, index, total, news, loading } = useScreenRotation();

  return (
    <div className="fixed inset-0 bg-[#F8FAF5] text-[hsl(var(--brand-ink))] overflow-hidden flex flex-col" data-testid="screen-vertical">
      <header className="flex items-center justify-between px-8 pt-6 pb-3 shrink-0 gap-4">
        <ScreenLogoBar size="md" />
        <div className="text-right shrink-0">
          <p className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">Newsticker</p>
          <p className="text-sm text-neutral-700 mt-0.5">{total > 0 ? `${index + 1} / ${total}` : ""}</p>
        </div>
      </header>

      <main className="flex-1 relative overflow-hidden min-h-0">
        {loading && <LoadingSlide orientation="vertical" />}
        {!loading && current === null && news.length === 0 && <EmptySlide orientation="vertical" />}
        {current && current.type === "news" && <NewsSlideVertical item={current.payload} key={`n-${index}`} />}
        {current && current.type === "map" && <MapSlide sperrungen={current.sperrungen} dataTestId="map-slide-vertical" key={`m-${index}`} />}
      </main>

      <div className="flex items-center justify-center gap-2 py-3 shrink-0">
        {total > 0 && Array.from({ length: Math.min(total, 12) }).map((_, i) => {
          const offset = total > 12 ? Math.floor(index / 12) * 12 : 0;
          return <ProgressDot key={i} active={(i + offset) === index} />;
        })}
      </div>

      {/* Skyline — natural height, always full */}
      <ScreenSkyline />
    </div>
  );
}

function NewsSlideVertical({ item }) {
  return (
    <div className="absolute inset-0 flex flex-col px-10 py-6 screen-fade-in">
      {/* Image (~58% of stage height) */}
      <div className="relative overflow-hidden rounded-sm shadow-[0_24px_60px_-30px_rgba(0,0,0,0.30)] bg-neutral-100" style={{ flexBasis: "58%", flexShrink: 0 }}>
        {item.image_url ? (
          <img src={item.image_url} alt="" className="absolute inset-0 w-full h-full object-cover" />
        ) : (
          <FallbackBackground />
        )}
      </div>

      {/* Text block — generously sized so it fills remaining space */}
      <div className="flex-1 flex flex-col justify-start pt-10 min-h-0">
        <p className="uppercase tracking-[0.2em] text-[hsl(var(--brand-green-strong))] mb-3 font-medium" style={{ fontSize: "clamp(0.8rem, 1.1vh, 1rem)" }}>
          Aktuelles aus Detmold
        </p>
        {item.pub_date && (
          <p className="text-neutral-500 inline-flex items-center gap-2 mb-5" style={{ fontSize: "clamp(0.95rem, 1.3vh, 1.2rem)" }}>
            <CalendarDays className="h-4 w-4" /> {formatPubDate(item.pub_date)}
          </p>
        )}
        <h2 className="font-semibold leading-[1.05] mb-6 tracking-tight" style={{ fontSize: "clamp(2.6rem, 4.4vh, 4.6rem)" }}>
          {item.title}
        </h2>
        {item.description && (
          <p className="text-neutral-700 leading-relaxed line-clamp-5" style={{ fontSize: "clamp(1.15rem, 1.9vh, 1.7rem)" }}>
            {item.description}
          </p>
        )}
      </div>
    </div>
  );
}
